import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateTime, formatNumber } from "../../lib/format";
import { auditLogs, onlineMismatchCount, productRows } from "../../lib/selectors";
import type { AnyRecord, AppData } from "../../lib/types";

export function ReportsPage({ data }: { data: AppData }) {
  const rows = reportRows(data);
  const columns: ColumnDef<AnyRecord>[] = [
    {
      accessorKey: "name",
      header: "리포트",
      cell: ({ row }) => (
        <div className="cell-primary">
          <strong>{row.original.name}</strong>
          <span>{row.original.description}</span>
        </div>
      ),
      size: 320
    },
    { accessorKey: "status", header: "상태", cell: ({ row }) => <StatusBadge tone={row.original.tone}>{row.original.status}</StatusBadge> },
    { accessorKey: "updatedAt", header: "최근 기준", cell: ({ row }) => row.original.updatedAt },
    { id: "action", header: "작업", cell: ({ row }) => <Link className="table-link" to={String(row.original.target)}>열기</Link> }
  ];

  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>운영 리포트</h2>
            <p>차트보다 먼저, 매일 확인해야 하는 재고/주문/외부 반영 상태를 리포트 큐로 관리합니다.</p>
          </div>
        </div>
        <DataTable columns={columns} data={rows} getRowId={(row) => String(row.key)} emptyTitle="표시할 리포트가 없습니다." />
      </section>
    </div>
  );
}

function reportRows(data: AppData): AnyRecord[] {
  const lowStock = productRows(data).reduce((count, product) => count + product.rows.filter((row) => Number(row.availableToSell || 0) <= 2).length, 0);
  const mismatch = onlineMismatchCount(data);
  const latestAudit = auditLogs(data)[0];
  return [
    {
      key: "inventory-risk",
      name: "재고 리스크",
      description: `저재고 SKU ${formatNumber(lowStock)}개`,
      status: lowStock ? "확인 필요" : "정상",
      tone: lowStock ? "warning" : "success",
      updatedAt: latestAudit?.createdAt ? formatDateTime(latestAudit.createdAt) : "-",
      target: "/inventory?view=low"
    },
    {
      key: "online-mismatch",
      name: "온라인몰 수량 차이",
      description: `백오피스 기준과 온라인몰 수량 차이 ${formatNumber(mismatch)}건`,
      status: mismatch ? "확인 필요" : "정상",
      tone: mismatch ? "warning" : "success",
      updatedAt: latestAudit?.createdAt ? formatDateTime(latestAudit.createdAt) : "-",
      target: "/inventory?view=online_mismatch"
    },
    {
      key: "audit",
      name: "감사 로그",
      description: "누가 무엇을 바꿨는지 확인",
      status: "운영 중",
      tone: "info",
      updatedAt: latestAudit?.createdAt ? formatDateTime(latestAudit.createdAt) : "-",
      target: "/settings?view=audit"
    }
  ];
}
